const STORAGE_KEY = "admin_session_token";

/**
 * The admin session token lives in sessionStorage, not a cookie. See
 * src/auth/sessions.ts on the backend for the full reasoning: a
 * cross-site cookie between Cloudflare Pages and Workers is fragile in
 * ways no Set-Cookie attribute fully fixes (SameSite requirements, and
 * separately, browser third-party-cookie blocking that ignores SameSite
 * entirely). A bearer token in a custom header sidesteps both.
 *
 * sessionStorage (not localStorage) is a deliberate choice: it survives
 * a page refresh within the same tab (so reloading doesn't log you out),
 * but is cleared when the tab closes and is never sent automatically the
 * way a cookie would be. It IS readable by any JS running on this page,
 * which an HttpOnly cookie would not have been — if this page ever had
 * an XSS vulnerability, the token would be exposed. That's weighed
 * against a session transport that silently fails in Safari and an
 * increasing share of other browsers by default; see the comment in
 * src/auth/sessions.ts for the full trade-off.
 */
export function getToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable (some private-browsing modes, quota
    // exhaustion). Treat as "no session" rather than throwing.
    return null;
  }
}

export function setToken(token: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // The session just won't survive a refresh in this case; the current
    // page's in-memory auth state (set right after this call) still works.
  }
}

export function clearToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}
